import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import Loader from '@/components/Loader';
import { LINKS } from '@/constants/links';
import { addSearchParams } from '@/utils/fetcher';
import { getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { NextRouter, useRouter } from 'next/router';
import { UAParser } from 'ua-parser-js';
import { COUNTRY_PUBLIC_LINK } from '@/constants/country';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { Link } from '@/components/links';
import { useTranslation } from 'react-i18next';
function clearSource(source: string) {
  return source.replace('https://', '').replace('www.', '');
}
const TIMEOUT = 8000;
const redirectWithTimeout = async (router: NextRouter) => {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const timeoutIdAbort = setTimeout(() => controller.abort(), TIMEOUT);
    let timeoutIdRedirect = null;
    try {
      const queryCopy = {
        ...router.query
      };
      queryCopy.utm_medium = 'referral';
      queryCopy.utm_source = clearSource(queryCopy.utm_source as string || document.referrer as string);
      queryCopy.redir = '1';
      const query = queryCopy;
      const device = new UAParser().getDevice();
      const baseUrl = COUNTRY_PUBLIC_LINK[process.env.NEXT_PUBLIC_LANGUAGE];
      if (device.type === 'mobile') {
        if (device.vendor === 'Apple') {
          window.location.replace(addSearchParams(`${baseUrl}/aplikace/iphone`, {
            ...query
          }));
        } else {
          window.location.replace(addSearchParams(`${baseUrl}/aplikace/android`, {
            ...query
          }));
        }
      } else {
        window.location.replace(addSearchParams(LINKS().mobileApp, {
          ...query
        }));
      }
      if (controller.signal.aborted) {
        reject(new Error('Redirect aborted due to timeout'));
        return;
      }
      timeoutIdRedirect = setTimeout(() => resolve('Redirect successful'), TIMEOUT / 2);
    } catch (error) {
      reject(error);
      timeoutIdRedirect && clearTimeout(timeoutIdRedirect);
    } finally {
      clearTimeout(timeoutIdAbort);
    }
  });
};
export default function Page() {
  const $ = _c(12);
  usePageView("app-store-redirect");
  const router = useRouter();
  const {
    locale
  } = useRouter();
  const {
    t
  } = useTranslation("core");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = QUERY_KEYS.appstoreRedirect();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== router) {
    t1 = {
      queryKey: t0,
      queryFn: () => redirectWithTimeout(router),
      retry: false
    };
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    isLoading
  } = useQuery(t1);
  if (isLoading) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Loader />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Loader hideLabel={true} />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== locale) {
    t3 = LINKS(locale);
    $[5] = locale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = t3.mobileApp;
  let t5;
  if ($[7] !== t) {
    t5 = t("continue");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3.mobileApp || $[10] !== t5) {
    t6 = <>{t2}<Link to={t4} analyticsParam="appStoreManualRedirect" testId="appStoreManualRedirect" className="flex justify-center">{t5}</Link></>;
    $[9] = t3.mobileApp;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
export const getStaticProps: GetStaticProps = async ctx => {
  return {
    props: await getIntlProps(ctx, ['core'])
  };
};